// Every write the portal makes. Admin operations run as client-side
// transactions (firestore.rules only lets admins make them); issuing a key
// against a quota goes through the issueLicense Cloud Function so that
// distributors never get write access to licences or quotas.
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  increment,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { sendSignInLinkToEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";
import { quotaBucket } from "../config";
import { generateLicenseKey } from "./licenses";

const activityRef = () => doc(collection(db, "activity"));

const cleanEmail = (email) => (email || "").trim().toLowerCase();

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function assertEmail(email) {
  if (!EMAIL_PATTERN.test(email))
    throw new Error(`"${email}" isn't a valid email address.`);
}

// ---------------------------------------------------------------------------
// Licences

const BATCH_CHUNK = 100;

// Creates stock keys (not tied to a distributor quota), optionally for one
// of LiFE's direct customers.
export async function generateLicenses({
  productId,
  type,
  count,
  distributorLabel = "",
  region = "",
  customer = null,
  development = false,
  actor,
}) {
  const created = [];
  while (created.length < count) {
    const size = Math.min(BATCH_CHUNK, count - created.length);
    let keys;
    for (let attempt = 0; attempt < 3 && !keys; attempt++) {
      const candidates = new Set();
      while (candidates.size < size) candidates.add(generateLicenseKey());
      const refs = [...candidates].map((key) => doc(db, productId, key));
      keys = await runTransaction(db, async (tx) => {
        const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
        if (snaps.some((snap) => snap.exists())) return null; // collision: retry
        refs.forEach((ref) =>
          tx.set(ref, {
            licenseKey: ref.id,
            status: "free",
            type,
            createdOn: serverTimestamp(),
            createdBy: actor,
            expirydate: null,
            registeredDate: null,
            distributor: distributorLabel.trim(),
            region: region.trim(),
            customer: customer?.name || "",
            customerEmail: customer?.email || "",
            customerId: customer?.id || null,
            location: customer?.location || "",
            remarks: "",
            distributorId: null,
            development,
            // A key generated for a direct customer counts as issued to them.
            issuedAt: customer ? serverTimestamp() : null,
            issuedBy: customer ? actor : null,
            // Indexed helper fields, so the keys appear in filtered lists
            // straight away (Cloud Functions add the search tokens).
            displayStatus: customer ? "issued" : "available",
            expirySort: "9999-12-31",
            issued: !!customer,
            linked: !!customer,
          }),
        );
        tx.set(activityRef(), {
          action: "generate",
          at: serverTimestamp(),
          actor,
          product: productId,
          type,
          count: size,
          distributorName: distributorLabel.trim(),
          customer: customer?.name || "",
          development,
        });
        return refs.map((ref) => ref.id);
      });
    }
    if (!keys)
      throw new Error("Could not generate unique keys, please try again.");
    created.push(...keys);
  }
  return created;
}

export async function updateLicenseDetails(productId, licenseId, fields) {
  const allowed = ["customer", "customerEmail", "location", "remarks"];
  const updates = {};
  allowed.forEach((field) => {
    if (field in fields) updates[field] = (fields[field] || "").trim();
  });
  // Link to (or unlink from) a customer record of the same distributor.
  if ("customerId" in fields) updates.customerId = fields.customerId || null;
  await updateDoc(doc(db, productId, licenseId), updates);
}

// Marks (or unmarks) keys as development keys, in batches.
export async function setDevelopment({
  productId,
  licenseIds,
  development,
  actor,
}) {
  const CHUNK = 400;
  for (let i = 0; i < licenseIds.length; i += CHUNK) {
    const ids = licenseIds.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    ids.forEach((id) => batch.update(doc(db, productId, id), { development }));
    batch.set(activityRef(), {
      action: "development",
      at: serverTimestamp(),
      actor,
      product: productId,
      count: ids.length,
      development,
      licenseKey: ids.length === 1 ? ids[0] : null,
    });
    await batch.commit();
  }
}

export async function extendLicense({
  productId,
  licenseId,
  expirydate,
  actor,
}) {
  await runTransaction(db, async (tx) => {
    const ref = doc(db, productId, licenseId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("This key no longer exists.");
    const previous = snap.data().expirydate || null;
    tx.update(ref, { expirydate });
    tx.set(activityRef(), {
      action: "extend",
      at: serverTimestamp(),
      actor,
      product: productId,
      licenseKey: licenseId,
      from: previous,
      to: expirydate,
      distributorId: snap.data().distributorId || null,
    });
  });
}

// Deletes a key that hasn't been activated. Keys issued against a quota are
// "revoked": the distributor gets the quota back.
export async function removeLicense({ productId, licenseId, actor }) {
  await runTransaction(db, async (tx) => {
    const ref = doc(db, productId, licenseId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("This key no longer exists.");
    const license = snap.data();
    if (license.status !== "free") {
      throw new Error("Activated keys can't be deleted.");
    }
    const distributorRef =
      license.distributorId && license.issuedAt
        ? doc(db, "distributors", license.distributorId)
        : null;
    const distributorSnap = distributorRef
      ? await tx.get(distributorRef)
      : null;

    tx.delete(ref);
    if (distributorSnap?.exists()) {
      const bucket = quotaBucket(productId, license.type);
      tx.update(distributorRef, {
        [`quota.${bucket}.issued`]: increment(-1),
        updatedAt: serverTimestamp(),
      });
    }
    tx.set(activityRef(), {
      action: distributorRef ? "revoke" : "delete",
      at: serverTimestamp(),
      actor,
      product: productId,
      type: license.type || null,
      licenseKey: licenseId,
      customer: license.customer || "",
      distributorId: license.distributorId || null,
      distributorName: license.distributor || "",
    });
  });
}

// Calls the issueLicense Cloud Function. Distributors issue against their own
// quota; admins pass distributorId to issue on a distributor's behalf.
export async function issueLicense(payload) {
  const call = httpsCallable(functions, "issueLicense");
  const result = await call(payload);
  return result.data;
}

export function describeIssueError(error) {
  const code = (error?.code || "").replace("functions/", "");
  switch (code) {
    case "resource-exhausted":
      return "There are no keys left for this type. Contact LiFE to top up your quota.";
    case "permission-denied":
    case "unauthenticated":
      return error.message || "Your account isn't allowed to issue keys.";
    case "invalid-argument":
    case "failed-precondition":
    case "not-found":
      return error.message;
    case "unavailable":
    case "deadline-exceeded":
      return "Couldn't reach the server. Check your connection and try again.";
    default:
      return "Something went wrong while issuing the key. Please try again.";
  }
}

// ---------------------------------------------------------------------------
// Distributors

export async function createDistributor({ fields, loginEmail, actor }) {
  const ref = await addDoc(collection(db, "distributors"), {
    ...trimFields(fields),
    active: true,
    quota: {},
    createdAt: serverTimestamp(),
    createdBy: actor,
    updatedAt: serverTimestamp(),
  });
  if (loginEmail) {
    await addPortalUser({ email: loginEmail, distributorId: ref.id, actor });
  }
  return ref.id;
}

export async function updateDistributor(distributorId, fields) {
  await updateDoc(doc(db, "distributors", distributorId), {
    ...trimFields(fields),
    updatedAt: serverTimestamp(),
  });
}

function trimFields(fields) {
  return Object.fromEntries(
    Object.entries(fields).map(([k, v]) => [
      k,
      typeof v === "string" ? v.trim() : v,
    ]),
  );
}

// delta > 0 allocates more keys, delta < 0 takes unissued keys back.
export async function adjustQuota({
  distributorId,
  productId,
  type,
  delta,
  note,
  actor,
}) {
  if (!Number.isInteger(delta) || delta === 0) {
    throw new Error("Enter a whole number of keys.");
  }
  const bucket = quotaBucket(productId, type);
  const ref = doc(db, "distributors", distributorId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Distributor not found.");
    const current = snap.data().quota?.[bucket] || {};
    const issued = current.issued || 0;
    const allocated = (current.allocated || 0) + delta;
    if (allocated < issued) {
      throw new Error(
        `${issued} keys of this type are already issued, so the quota can't go below ${issued}.`,
      );
    }
    tx.update(ref, {
      [`quota.${bucket}`]: { allocated, issued },
      updatedAt: serverTimestamp(),
    });
    tx.set(activityRef(), {
      action: "allocate",
      at: serverTimestamp(),
      actor,
      distributorId,
      distributorName: snap.data().name || "",
      product: productId,
      type,
      delta,
      allocated,
      note: (note || "").trim(),
    });
  });
}

// ---------------------------------------------------------------------------
// Customer records (customers/{id}), kept by distributors; optional.

function customerFields(fields) {
  return {
    name: (fields.name || "").trim(),
    email: (fields.email || "").trim(),
    phone: (fields.phone || "").trim(),
    location: (fields.location || "").trim(),
    notes: (fields.notes || "").trim(),
  };
}

// distributorId null = one of LiFE's direct customers (admins only).
export async function createCustomer({ distributorId, fields, actor }) {
  const ref = await addDoc(collection(db, "customers"), {
    distributorId: distributorId || null,
    ...customerFields(fields),
    createdAt: serverTimestamp(),
    createdBy: actor,
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCustomer(customerId, fields) {
  await updateDoc(doc(db, "customers", customerId), {
    ...customerFields(fields),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCustomer(customerId) {
  await deleteDoc(doc(db, "customers", customerId));
}

// ---------------------------------------------------------------------------
// Portal access (users/{email})

// Emails a Firebase sign-in link to a new login. Opening it signs them in;
// their Firebase account is created on that first sign-in.
export async function sendInvite(email) {
  await sendSignInLinkToEmail(auth, email, {
    url: `${window.location.origin}/login`,
    handleCodeInApp: true,
  });
}

export function inviteMessage({ email, distributorName }) {
  return [
    `You now have access to the LiFE KeyPortal${distributorName ? ` for ${distributorName}` : ""}.`,
    "",
    `1. Open ${window.location.origin}`,
    `2. Sign in as ${email}:`,
    '   - "Continue with Google" if this address is a Gmail / Google Workspace account, or',
    '   - type the address and press "Email me a link", then open the link we email you.',
    "",
    "No password needed. From there you can issue licence keys to your customers.",
  ].join("\n");
}

export async function addPortalUser({ email, distributorId, actor }) {
  const id = cleanEmail(email);
  assertEmail(id);
  const ref = doc(db, "users", id);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    const data = existing.data();
    if (data.role === "admin")
      throw new Error(`${id} is already a LiFE admin.`);
    if (data.distributorId && data.distributorId !== distributorId) {
      throw new Error(`${id} already has access for another distributor.`);
    }
  }
  await setDoc(ref, {
    role: "distributor",
    distributorId,
    active: true,
    addedBy: actor,
    addedAt: serverTimestamp(),
  });
  await logAccess({ action: "grant", email: id, distributorId, actor });
}

export async function addAdmin({ email, actor }) {
  const id = cleanEmail(email);
  assertEmail(id);
  const existing = await getDoc(doc(db, "users", id));
  if (existing.exists() && existing.data().role === "distributor") {
    throw new Error(
      `${id} is a distributor login. Remove it from the distributor first.`,
    );
  }
  await setDoc(doc(db, "users", id), {
    role: "admin",
    active: true,
    addedBy: actor,
    addedAt: serverTimestamp(),
  });
  await logAccess({ action: "grant", email: id, role: "admin", actor });
}

export async function removeUser({ email, distributorId = null, actor }) {
  await deleteDoc(doc(db, "users", email));
  await logAccess({ action: "revokeAccess", email, distributorId, actor });
}

function logAccess({
  action,
  email,
  distributorId = null,
  role = "distributor",
  actor,
}) {
  return setDoc(activityRef(), {
    action,
    at: serverTimestamp(),
    actor,
    email,
    role,
    distributorId,
  });
}
